import { FC, ReactNode } from 'react';

import { useSession } from '@eneco/auth';
import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import { addMonths } from '@eneco/common/date';
import {
  DisplayModeDto,
  PeakAverageResponse,
  PeakResponse,
  TimeFrameDto,
  getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierCaptar,
  getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierCaptarAverage,
} from '@eneco/dc-be/client';
import useAuthenticatedDCBE from '@eneco/dc-be/hooks/useAuthenticatedDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';

import DataErrorNotification from '../../../../components/DataErrorNotification';
import { useAddressOptions } from '../../../../hooks/useAddressOptions';

interface PeakValuesConsumptionDataProps {
  timeFrame: TimeFrameDto;
  startDate: string;
  endDate: string;
  children: (data: {
    consumptionData: PeakResponse | null;
    peakValueAverage: PeakAverageResponse | null;
    isLoading: boolean;
  }) => ReactNode;
  signal?: AbortSignal;
}

const PeakValuesConsumptionData: FC<PeakValuesConsumptionDataProps> = ({
  timeFrame,
  startDate,
  endDate,
  children,
  signal,
}) => {
  const { data: session } = useSession();
  const {
    selectedAccount: { crmAccountNumber: accountNumber },
  } = useSelfServiceAccount();
  const { addressIdentifier } = useAddressOptions();

  const query = {
    consumptionType: 'PeakValues' as const,
    displayMode: 'Consumption' as DisplayModeDto,
    timeFrame: timeFrame,
    startDate,
    endDate,
  };

  const { data, isLoading, error } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierCaptar,
    {
      path: {
        accountNumber,
        addressIdentifier,
      },
      query,
      signal,
    },
    [
      `/accounts/${accountNumber}/delivery-addresses/${addressIdentifier}/consumption`,
      timeFrame,
      startDate,
      endDate,
      'PeakValues',
    ],
    session,
  );

  const today = new Date();
  const oneYearAgo = addMonths(today, -11);

  const queryPeakValueAverage = {
    consumptionType: 'PeakValues' as const,
    displayMode: 'Consumption' as DisplayModeDto,
    startDate: oneYearAgo.toISOString().split('T')[0],
    endDate: today.toISOString().split('T')[0],
  };

  const { data: peakValueAvererageData } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierCaptarAverage,
    {
      path: {
        accountNumber,
        addressIdentifier,
      },
      query: queryPeakValueAverage,
    },
    [
      `/accounts/{accountNumber}/delivery-addresses/{addressIdentifier}/captar/average`,
      oneYearAgo.toISOString().split('T')[0],
      today.toISOString().split('T')[0],
      'PeakValues',
    ],
    session,
  );

  const consumptionData = unwrapData(data);
  const peakValueAverage = unwrapData(peakValueAvererageData);

  if (error) {
    return <DataErrorNotification />;
  }

  return <>{children({ consumptionData, peakValueAverage, isLoading })}</>;
};

export default PeakValuesConsumptionData;
